import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router';
import { clearToastMessages } from 'src/actions';
import { useAccountSelector } from 'src/hooks';
import { MAlert } from 'src/lib';
import { ScreenBreakpoint } from 'src/styles';
import { isAccountPersonalInformationComplete } from 'src/utils';

import { MyAccountLayout } from '../../../lib/Layout/MyAccountLayout/MyAccountLayout';
import { MyAccountSidebarMainMenuItemKey } from '../../../lib/Layout/MyAccountLayout/MyAccountSidebar';

import { AccountHolderMailingAddressSection } from './AccountHolderMailingAddressSection';
import { AccountHolderPhysicalAddressSection } from './AccountHolderPhysicalAddressSection';
import { AccountHolderSection } from './AccountHolderSection';
import { AccountNumberSection } from './AccountNumberSection';
import { AccountTypeSection } from './AccountTypeSection';
import { EntityAccountInformationSection } from './EntityAccountInformationSection';
import { JointAccountHolderMailingAddressSection } from './JointAccountHolderMailingAddressSection';
import { JointAccountHolderPhysicalAddressSection } from './JointAccountHolderPhysicalAddressSection';
import { JointAccountHolderSection } from './JointAccountHolderSection';
import { OtherAccountHolderListSection } from './OtherAccountHolderListSection';

export const PersonalInformation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isPatchPersonalInformationSuccess: boolean = useSelector((state: any) =>
    Boolean(state.accountHolders.patch.__succeeded),
  );
  const isCreatePhysicalAddressSuccess: boolean = useSelector((state: any) =>
    Boolean(state.accountHolders.createPhysicalAddress.__succeeded),
  );
  const isPatchPhysicalAddressSuccess: boolean = useSelector((state: any) =>
    Boolean(state.accountHolders.patchPhysicalAddress.__succeeded),
  );
  const isCreateMailingAddressSuccess: boolean = useSelector((state: any) =>
    Boolean(state.accountHolders.createMailingAddress.__succeeded),
  );
  const isPatchMailingAddressSuccess: boolean = useSelector((state: any) =>
    Boolean(state.accountHolders.patchMailingAddress.__succeeded),
  );

  const { account } = useAccountSelector();

  const isMobile = useMediaQuery({ query: `(max-width: ${ScreenBreakpoint.mobile.max})` });

  const hasAnyCreateRequest = () => isCreatePhysicalAddressSuccess || isCreateMailingAddressSuccess;

  const hasAnyPatchRequest = () =>
    isPatchPersonalInformationSuccess || isPatchPhysicalAddressSuccess || isPatchMailingAddressSuccess;

  const hasAnyUpsertRequest = () => hasAnyCreateRequest() || hasAnyPatchRequest();

  const shouldSendAnalyticsEvent = () =>
    !account?.accountId && hasAnyCreateRequest() && isAccountPersonalInformationComplete(account);

  const shouldRedirectToTrustedContact = () =>
    !account?.accountId && hasAnyUpsertRequest() && isAccountPersonalInformationComplete(account);

  const getTitle = () => {
    if (isMobile) {
      return;
    }

    if (!account) {
      return 'Account Type';
    }

    if (account.type.isEntity && !account.isEntityAccountTypeActive) {
      return 'Entity';
    }

    return 'Personal';
  };

  const shouldDisplayAccountType = () => {
    if (account?.isEntityAccountTypeActive) {
      return false;
    }

    return true;
  };

  const shouldDisplayAccountNumber = () => {
    if (account?.accountNumber) {
      return shouldDisplayAccountType();
    }

    return false;
  };

  const shouldDisplayAccountHolderInformation = () => {
    if (!account?.type?.value) {
      return false;
    }

    if (account?.type.isEntity && !account?.isEntityAccountTypeActive) {
      return false;
    }

    return true;
  };

  const shouldDisplayJointAccountHolderInformation = () => {
    if (account?.type.isJoint || account?.type.isUgma) {
      return true;
    }

    if (account?.isEntityAccountTypeActive && account?.secondaryAccountHolder) {
      return true;
    }

    return false;
  };

  const shouldDisplayEntityAccountInformation = () => account?.type.isEntity && !account?.isEntityAccountTypeActive;

  const shouldDisplayOtherAccountHolderListSection = () => {
    return (
      account?.isEntityAccountTypeActive && account?.otherAccountHolders && account?.otherAccountHolders?.length > 0
    );
  };

  useEffect(() => {
    if (shouldSendAnalyticsEvent()) {
      window.gtag('event', 'account_personal_information_complete');
    }
  }, [isCreatePhysicalAddressSuccess, isCreateMailingAddressSuccess]);

  useEffect(() => {
    if (shouldRedirectToTrustedContact()) {
      navigate(`/accounts/trusted-contact`);
    }
  }, [
    isPatchPersonalInformationSuccess,
    isCreatePhysicalAddressSuccess,
    isPatchPhysicalAddressSuccess,
    isCreateMailingAddressSuccess,
    isPatchMailingAddressSuccess,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearToastMessages());
    };
  }, []);

  return (
    <MyAccountLayout
      sidebarMenuItemKey={MyAccountSidebarMainMenuItemKey.PersonalInformation}
      title={getTitle()}
      testId={account ? 'personal' : 'account-type'}>
      {account?.lastFailedLinkAttemptError && (
        <MAlert
          type='error'
          description={`You cannot edit account information until your ${account.customTypeLabel} account has been successfully linked.`}
        />
      )}
      {shouldDisplayAccountType() && <AccountTypeSection />}
      {shouldDisplayAccountNumber() && <AccountNumberSection />}
      {shouldDisplayAccountHolderInformation() && <AccountHolderSection />}
      {shouldDisplayAccountHolderInformation() && <AccountHolderPhysicalAddressSection />}
      {shouldDisplayAccountHolderInformation() && <AccountHolderMailingAddressSection />}
      {shouldDisplayJointAccountHolderInformation() && <JointAccountHolderSection />}
      {shouldDisplayJointAccountHolderInformation() && <JointAccountHolderPhysicalAddressSection />}
      {shouldDisplayJointAccountHolderInformation() && <JointAccountHolderMailingAddressSection />}
      {shouldDisplayEntityAccountInformation() && <EntityAccountInformationSection />}
      {shouldDisplayOtherAccountHolderListSection() && <OtherAccountHolderListSection />}
    </MyAccountLayout>
  );
};
